import { Action, Dispatch } from 'redux';

import { COLLECTIONS, DRAWER_TYPES, TICKETS_URL } from 'appConstants';
import {
  clearCollection,
  getTicket,
  RetryableAction,
  setRecipientsFilterText,
  setUsersFilterText,
  setCommentUpdated,
  setRecipientsUpdated,
  setIsClosingError,
  setisTicketClosedSucess,
  setDrawerType,
} from 'reducers';
import { ApiRequest, BrowserUtils } from 'services';
import { Device } from 'types';

/* Types */

export interface TicketsAction extends Action, RetryableAction {
  readonly checked?: boolean;
  readonly favorite?: boolean;
  readonly ticketId?: string;
}

export interface Tickets {
  readonly checkedIds: string[];
  readonly favoriteOverrides: {};
}

/* Initial State */

const initialState = {
  checkedIds: [],
  favoriteOverrides: {},
} as Tickets;

/* Actions */

enum Actions {
  CLEAR_CHECKED_TICKETS = 'CLEAR_CHECKED_TICKETS',
  SET_FAVORITE_OVERRIDES = 'SET_FAVORITE_OVERRIDES',
  SET_TICKET_CHECK = 'SET_TICKET_CHECK',
  TOGGLE_TICKET_CHECK = 'TOGGLE_TICKET_CHECK',
}

const clearCheckedTickets = (): TicketsAction => ({
  type: Actions.CLEAR_CHECKED_TICKETS,
});

const setFavoriteOverride = (
  ticketId: string,
  favorite: boolean,
): TicketsAction => ({
  type: Actions.SET_FAVORITE_OVERRIDES,
  ticketId,
  favorite,
});

const setTicketCheck = (ticketId: string, checked: boolean): TicketsAction => ({
  type: Actions.SET_TICKET_CHECK,
  ticketId,
  checked,
});

const toggleTicketCheck = (ticketId: string): TicketsAction => ({
  type: Actions.TOGGLE_TICKET_CHECK,
  ticketId,
});

/* Reducers */

const ticketsReducer = (state = initialState, action: TicketsAction) => {
  let index: number = -1;
  let id: string = action.ticketId || '';

  switch (action.type) {
    case Actions.CLEAR_CHECKED_TICKETS:
      return {
        ...state,
        checkedIds: [],
      };
    case Actions.SET_FAVORITE_OVERRIDES:
      if (!id) {
        return state;
      }

      return {
        ...state,
        favoriteOverrides: {
          ...state.favoriteOverrides,
          [id]: action.favorite,
        },
      };
    case Actions.SET_TICKET_CHECK:
      const checkedIds = [...state.checkedIds];
      index = checkedIds.indexOf(id);
      if (action.checked && index === -1) {
        checkedIds.push(id);
      } else if (!action.checked && index > -1) {
        checkedIds.splice(index, 1);
      }

      return {
        ...state,
        checkedIds: [...checkedIds],
      };
    case Actions.TOGGLE_TICKET_CHECK:
      index = state.checkedIds.indexOf(id);
      const checked = [...state.checkedIds];
      if (index === -1) {
        checked.push(id);
      } else {
        checked.splice(index, 1);
      }

      return {
        ...state,
        checkedIds: [...checked],
      };
    default:
      return state;
  }
};

/* Thunk */

/** Reset Ticket Details state and fetch ticket */
const selectTicket = (ticketId: string) => async (
  dispatch: Dispatch<TicketsAction>,
) => {
  dispatch(clearCollection<Device>(COLLECTIONS.DEVICE));

  /** clear Recipients filter */
  dispatch(setRecipientsFilterText(''));

  /** Set comment processing flag to false on each ticket select */
  dispatch(setCommentUpdated(false));

  /** Set recipients processing flag to false on each ticket select */
  dispatch(setRecipientsUpdated(false));
  /** clear Add Users filter */
  dispatch(setUsersFilterText(''));

  /** hide close error error on closing */
  dispatch(setIsClosingError(false));

  /** hide close success on closing */
  dispatch(setisTicketClosedSucess(false));

  /** set ticket details */
  dispatch(getTicket(ticketId, true));

  if (BrowserUtils.isScreenSize768()) {
    dispatch(setDrawerType(DRAWER_TYPES.TICKET_DETAILS));
  } else {
    dispatch(setDrawerType(DRAWER_TYPES.NONE));
  }
};

const submitFavorite = (ticketId: string, favorite: boolean) => async (
  dispatch: Dispatch<TicketsAction>,
) => {
  const onError = () => console.error('Set Favorite Error');

  const onSuccess = () => dispatch(setFavoriteOverride(ticketId, favorite));

  const options = {
    onError,
    onSuccess,
  };

  if (favorite) {
    await ApiRequest.put(`${TICKETS_URL}/${ticketId}/favorite`, {}, options);
  } else {
    await ApiRequest.delete(`${TICKETS_URL}/${ticketId}/favorite`, options);
  }
};

export {
  clearCheckedTickets,
  selectTicket,
  setFavoriteOverride,
  setTicketCheck,
  submitFavorite,
  ticketsReducer,
  toggleTicketCheck,
};
